/**
 * ShareModal — crée un lien de partage public temporaire du projet courant.
 *
 * UX :
 *   1. Optionnel : l'utilisateur donne un titre au partage (visible côté client)
 *      et choisit une durée d'expiration (1-30 jours).
 *   2. Clic "Créer le lien" → call backend → reçoit l'URL.
 *   3. L'URL apparaît dans un champ readonly avec un bouton "Copier".
 *   4. L'utilisateur copie + envoie par WhatsApp/email/etc.
 *
 * Le projet partagé est un snapshot figé : modifier après partage n'affecte
 * pas le rendu côté client. Pour itérer, recréer un nouveau lien.
 */
import { useState } from 'react';
import { useLabelStore } from '../store/useLabelStore';
import { createShare } from '../api/shareClient';

export default function ShareModal({ isOpen, onClose }) {
  const getProjectData = useLabelStore(s => s.getProjectData);
  const [title, setTitle] = useState('');
  const [expiryDays, setExpiryDays] = useState(7);
  const [isCreating, setIsCreating] = useState(false);
  const [created, setCreated] = useState(null); // { token, url, expires_at } | null
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCreate = async () => {
    setIsCreating(true);
    setError(null);
    try {
      const projectData = getProjectData();
      const res = await createShare({
        projectData,
        title: title.trim() || null,
        expiryDays: parseInt(expiryDays, 10) || 7,
      });
      setCreated(res);
    } catch (e) {
      setError(e.message || 'Impossible de créer le lien');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopy = async () => {
    if (!created?.url) return;
    try {
      await navigator.clipboard.writeText(created.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = created.url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch (_) {}
      document.body.removeChild(ta);
    }
  };

  const handleReset = () => {
    setCreated(null);
    setTitle('');
    setExpiryDays(7);
    setError(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center modal-backdrop backdrop-blur-sm p-4"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-md bg-forge-bg border border-white/10 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/5 bg-black/20">
          <div className="flex items-center gap-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-forge-mauve">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
            </svg>
            <h2 className="text-[10px] font-mono font-bold uppercase tracking-[0.2em] text-white/70">Partager le projet</h2>
          </div>
          <button onClick={handleClose} className="w-6 h-6 rounded-md flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 transition-colors">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-5">
          {!created ? (
            // ─── Mode création ───
            <>
              <p className="text-[11px] font-sans text-white/60 leading-relaxed mb-4">
                Génère un lien public temporaire que tu peux envoyer à un client par mail ou WhatsApp.
                Il pourra <strong>visualiser l'étiquette en plein écran</strong> et choisir <strong>Valider</strong> ou <strong>Demander des modifications</strong>.
              </p>

              <label className="block mb-3">
                <span className="text-[9px] font-mono uppercase tracking-widest text-white/50 mb-1 block">
                  Titre (optionnel)
                </span>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Ex: Aromasense Vanille — validation Polyprint"
                  maxLength={255}
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-[12px] text-white/90 placeholder-white/25 outline-none focus:border-forge-mauve/40 transition-colors"
                />
              </label>

              <label className="block mb-5">
                <span className="text-[9px] font-mono uppercase tracking-widest text-white/50 mb-1 block">
                  Expiration
                </span>
                <select
                  value={expiryDays}
                  onChange={(e) => setExpiryDays(e.target.value)}
                  className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-[12px] text-white/90 outline-none focus:border-forge-mauve/40 transition-colors"
                >
                  <option value="1">1 jour</option>
                  <option value="3">3 jours</option>
                  <option value="7">7 jours (recommandé)</option>
                  <option value="14">14 jours</option>
                  <option value="30">30 jours (max)</option>
                </select>
              </label>

              {error && (
                <div className="mb-3 p-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-[10px] font-mono text-red-300">
                  ⚠ {error}
                </div>
              )}

              <button
                onClick={handleCreate}
                disabled={isCreating}
                className="w-full py-2.5 bg-forge-mauve/20 hover:bg-forge-mauve/30 border border-forge-mauve/40 hover:border-forge-mauve/60 rounded-lg text-[11px] font-mono uppercase tracking-widest text-forge-mauve transition-all disabled:opacity-50 disabled:cursor-wait"
              >
                {isCreating ? 'Création…' : 'Créer le lien'}
              </button>
            </>
          ) : (
            // ─── Mode lien créé ───
            <>
              <div className="mb-4 flex items-center gap-2">
                <span className="text-green-400">✓</span>
                <p className="text-[11px] font-sans text-white/85">Lien créé. Tu peux maintenant le copier et l'envoyer.</p>
              </div>

              <div className="mb-4">
                <span className="text-[9px] font-mono uppercase tracking-widest text-white/50 mb-1 block">
                  URL à partager
                </span>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={created.url}
                    readOnly
                    onFocus={(e) => e.target.select()}
                    className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-[10px] font-mono text-white/85 outline-none focus:border-forge-mauve/40"
                  />
                  <button
                    onClick={handleCopy}
                    className={`px-3 py-2 rounded-lg text-[10px] font-mono uppercase tracking-wider border transition-all ${
                      copied
                        ? 'bg-green-500/20 border-green-500/40 text-green-300'
                        : 'bg-forge-mauve/15 border-forge-mauve/40 text-forge-mauve hover:bg-forge-mauve/25'
                    }`}
                  >
                    {copied ? '✓ Copié' : '📋 Copier'}
                  </button>
                </div>
              </div>

              <div className="text-[9px] font-mono text-white/40 mb-4 space-y-1">
                <div>• Expire le {new Date(created.expires_at).toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' })}</div>
                <div>• Le projet partagé est un snapshot figé — toute modif ultérieure ne sera pas visible</div>
                <div>• Tu seras notifié quand le client validera ou demandera une modif</div>
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleReset}
                  className="flex-1 py-2 border border-white/10 rounded-lg text-[10px] font-mono uppercase tracking-wider text-white/60 hover:text-white hover:border-white/30 transition-all"
                >
                  Créer un autre lien
                </button>
                <button
                  onClick={handleClose}
                  className="flex-1 py-2 bg-forge-mauve/20 border border-forge-mauve/40 rounded-lg text-[10px] font-mono uppercase tracking-wider text-forge-mauve hover:bg-forge-mauve/30 transition-all"
                >
                  Terminé
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
